import React, { useEffect } from 'react';

import RetryNotification from '../RetryNotification/RetryNotification';

import { useTranslation } from '@hooks/useTranslations';
import AdobeTrackingService from '@services/AdobeTrackingService';
import { ResultCode, RetryPaymentMethod, RetryPaymentStatus } from '@types-internal';

export type RetryPaymentNotificationProps = {
  retryPaymentStatus?: RetryPaymentStatus;
  retryPaymentMethod?: RetryPaymentMethod;
  resultCode?: ResultCode;
  orderId?: string;
  currency?: string;
  sid?: string;
  siteId?: number;
  customerId?: string;
};

const RetryPaymentNotification: React.FC<RetryPaymentNotificationProps> = ({
  retryPaymentStatus,
  retryPaymentMethod,
  resultCode,
  orderId,
  currency,
  sid,
  siteId,
  customerId,
}) => {
  const { t } = useTranslation();
  const isKlarnaPaymentRefused =
    (retryPaymentMethod === 'KLARNA_PAY_LATER' || retryPaymentMethod === 'KLARNA_PAY_OVER_TIME') &&
    resultCode === 'REFUSED';
  useEffect(() => {
    const timer = setTimeout(() => {
      AdobeTrackingService.accountEventTracking('retry_payment_status', {
        payment_method: retryPaymentMethod,
        order_id: orderId,
        payment_status: retryPaymentStatus,
      });
    }, 5000);
    return () => clearTimeout(timer);
  }, [retryPaymentStatus, retryPaymentMethod, orderId]);

  switch (retryPaymentStatus) {
    case 'SUCCESSFUL':
      return (
        <RetryNotification
          id="retryPaymentSuccessfulNotification"
          ariaLabel={t('mzp.order.detail.retry.payment.notification.accessibility.label')}
          currency={currency}
          siteId={siteId}
          customerId={customerId}
          sid={sid}
          type="success">
          {t('mzp.order.detail.payment.retried.successful')}
        </RetryNotification>
      );
    case 'PROCESSING':
    case 'UNKNOWN':
    case 'SERVICE_NOT_AVAILABLE':
      return (
        <RetryNotification
          id="retryPaymentInfoNotification"
          ariaLabel={t('mzp.order.detail.retry.payment.notification.accessibility.label')}
          currency={currency}
          siteId={siteId}
          customerId={customerId}
          sid={sid}
          type="info">
          {t('mzp.order.detail.payment.retried.processing')}
        </RetryNotification>
      );
    case 'CANCELLED':
      return (
        <RetryNotification
          id="retryPaymentInfoNotification"
          ariaLabel={t('mzp.order.detail.retry.payment.notification.accessibility.label')}
          currency={currency}
          siteId={siteId}
          customerId={customerId}
          sid={sid}
          type="info">
          {t('mzp.order.detail.payment.retried.cancelled')}
        </RetryNotification>
      );
    case 'UNSUCCESSFUL':
      if (isKlarnaPaymentRefused) {
        return (
          <RetryNotification
            id="retryPaymentErrorNotification"
            ariaLabel={t('mzp.order.detail.retry.payment.notification.accessibility.label')}
            currency={currency}
            siteId={siteId}
            customerId={customerId}
            sid={sid}
            type="warning">
            {t('mzp.order.detail.payment.retried.unsuccessful.klarna')}
          </RetryNotification>
        );
      } else {
        return (
          <RetryNotification
            id="retryPaymentErrorNotification"
            ariaLabel={t('mzp.order.detail.retry.payment.notification.accessibility.label')}
            currency={currency}
            siteId={siteId}
            customerId={customerId}
            sid={sid}
            type="warning">
            {t('mzp.order.detail.payment.retried.unsuccessful')}
          </RetryNotification>
        );
      }
  }

  return null;
};

export default RetryPaymentNotification;
